import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, X, Loader2, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import api from '@/services/api';

/**
 * Searchable multi-select picker for products / categories / brands.
 * Props:
 *   kind: 'product' | 'category' | 'brand'
 *   value: string[] (selected ids)
 *   onChange: (ids: string[]) => void
 *   testPrefix: string — used for data-testids
 *   placeholder?: string
 *   allowEmpty?: boolean — show helper text when array is empty (e.g. "empty = all")
 */
export default function EntityPicker({ kind, value = [], onChange, testPrefix, placeholder, allowEmpty = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState({}); // id → full item for chip display
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const endpoint = useMemo(() => {
    if (kind === 'product') return '/ecom/catalog/products';
    if (kind === 'category') return '/ecom/catalog/categories';
    if (kind === 'brand') return '/ecom/catalog/brands';
    return null;
  }, [kind]);

  // Hydrate selected chips on mount / when value changes from outside
  useEffect(() => {
    const missing = value.filter(id => !selectedItems[id]);
    if (missing.length === 0 || !endpoint) return;
    (async () => {
      try {
        if (kind === 'product') {
          const res = await api.get(endpoint, { params: { page_size: 200 } });
          const map = {};
          (res.data.items || []).forEach(p => { if (missing.includes(p.id)) map[p.id] = p; });
          setSelectedItems(prev => ({ ...prev, ...map }));
        } else {
          const res = await api.get(endpoint);
          const list = res.data.items || res.data || [];
          const map = {};
          list.forEach(x => { if (missing.includes(x.id)) map[x.id] = x; });
          setSelectedItems(prev => ({ ...prev, ...map }));
        }
      } catch { /* silent */ }
    })();
     
  }, [value.join(','), endpoint, kind]);

  // Search on typing
  useEffect(() => {
    if (!open || !endpoint) return;
    let ignore = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        let items = [];
        if (kind === 'product') {
          const res = await api.get(endpoint, { params: { q: query || undefined, page_size: 20 } });
          items = res.data.items || [];
        } else {
          const res = await api.get(endpoint);
          const list = res.data.items || res.data || [];
          const q = query.toLowerCase().trim();
          items = q ? list.filter(x => (x.name || '').toLowerCase().includes(q)) : list;
          items = items.slice(0, 30);
        }
        if (!ignore) setResults(items);
      } catch { if (!ignore) setResults([]); }
      finally { if (!ignore) setLoading(false); }
    }, 200);
    return () => { ignore = true; clearTimeout(t); };
  }, [query, open, endpoint, kind]);

  const toggle = (item) => {
    const has = value.includes(item.id);
    const next = has ? value.filter(id => id !== item.id) : [...value, item.id];
    onChange(next);
    if (!has) setSelectedItems(prev => ({ ...prev, [item.id]: item }));
  };

  const remove = (id) => onChange(value.filter(x => x !== id));

  const renderChip = (id) => {
    const item = selectedItems[id];
    const label = item?.name || id.slice(0, 8);
    const img = item?.images?.[0] || item?.image_url || item?.logo_url;
    return (
      <span key={id} className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 bg-slate-100 rounded-full text-xs" data-testid={`${testPrefix}-chip-${id}`}>
        {img ? <img src={img} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="h-5 w-5 rounded-full bg-slate-300" />}
        <span className="max-w-[140px] truncate">{label}</span>
        <button type="button" onClick={() => remove(id)} className="ml-0.5 opacity-60 hover:opacity-100" data-testid={`${testPrefix}-remove-${id}`}>
          <X className="h-3 w-3" />
        </button>
      </span>
    );
  };

  return (
    <div className="space-y-2" data-testid={`${testPrefix}-picker`}>
      {/* selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testPrefix}-chips`}>
          {value.map(renderChip)}
        </div>
      )}

      {/* search box */}
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder={placeholder || `Search ${kind === 'category' ? 'categories' : `${kind}s`}…`}
          data-testid={`${testPrefix}-search`}
        />
        {open && (
          <Card className="absolute z-30 top-full left-0 right-0 mt-1 max-h-72 overflow-y-auto shadow-lg" onMouseDown={(e) => e.preventDefault()}>
            <div className="p-1">
              {loading ? (
                <div className="p-3 text-sm text-muted-foreground text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>
              ) : results.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground text-center">No {kind}s found</div>
              ) : (
                results.map(item => {
                  const selected = value.includes(item.id);
                  const img = item?.images?.[0] || item?.image_url || item?.logo_url;
                  return (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => toggle(item)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm hover:bg-slate-100 ${selected ? 'bg-emerald-50' : ''}`}
                      data-testid={`${testPrefix}-option-${item.id}`}
                    >
                      {img ? <img src={img} alt="" className="h-8 w-8 rounded object-cover" /> : <span className="h-8 w-8 rounded bg-slate-200" />}
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium">{item.name}</div>
                        {kind === 'product' && (
                          <div className="text-xs text-muted-foreground">
                            {item.sku ? `${item.sku} · ` : ''}₹{item.selling_price ?? item.mrp}
                          </div>
                        )}
                      </div>
                      {selected && <Check className="h-4 w-4 text-emerald-600" />}
                    </button>
                  );
                })
              )}
            </div>
            <div className="border-t px-2 py-1.5 flex justify-between items-center text-xs text-muted-foreground">
              <span>{value.length} selected</span>
              <button type="button" className="hover:text-foreground" onClick={() => setOpen(false)} data-testid={`${testPrefix}-close`}>Close</button>
            </div>
          </Card>
        )}
      </div>

      {allowEmpty && value.length === 0 && (
        <p className="text-xs text-muted-foreground">Empty selection = show all live {kind === 'category' ? 'categories' : `${kind}s`} from the ERP.</p>
      )}
    </div>
  );
}
